// Data access for The Edit.
//
// Posts live in a Cloudflare Worker (workers/edit-api) backed by its own KV.
// The browser never talks to it directly — requests go same-origin through the
// Pages Function at /api/edit/*, which proxies to the worker. That keeps CORS
// out of the picture and means the worker's hostname can change without a
// frontend release.
//
// Every fetch degrades to the static ARTICLES list in data/articles.js, so an
// unreachable worker costs us freshness, never a blank page.

import { useEffect, useState } from "react";
import { ARTICLES, mergeArticles, normalizeRemote } from "../data/articles.js";

export const EDIT_API = "/api/edit/posts";

// The list, newest first, with the static fallbacks merged in behind.
export function useArticles() {
  const [articles, setArticles] = useState(ARTICLES);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    fetch(EDIT_API)
      .then(r => r.ok ? r.json() : [])
      .then(remote => { if (live) setArticles(mergeArticles(Array.isArray(remote) ? remote : [], ARTICLES)); })
      .catch(() => {})
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, []);
  return { articles, loaded };
}

// One post with its full body. The index endpoint carries summaries only, so a
// article page always needs this second call to get `body`.
export function useArticle(category, slug) {
  const fallback = ARTICLES.find(a => a.category === category && a.slug === slug) || null;
  const [article, setArticle] = useState(fallback);
  const [state, setState] = useState(fallback ? "ready" : "loading");
  useEffect(() => {
    let live = true;
    if (!slug) { setState("missing"); return; }
    fetch(`${EDIT_API}/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : null)
      .then(post => {
        if (!live) return;
        const normalized = post ? normalizeRemote(post) : null;
        // Guard the URL: a post filed under a different category than the one
        // in the address bar must not render here, or two URLs serve one post.
        if (normalized && normalized.category === category) { setArticle(normalized); setState("ready"); }
        else if (fallback) { setState("ready"); }
        else { setState("missing"); }
      })
      .catch(() => { if (live) setState(fallback ? "ready" : "missing"); });
    return () => { live = false; };
  }, [category, slug]);
  return { article, state };
}

export const formatDate = v => {
  if (!v) return "";
  // A bare "2026-04-18" parses as UTC midnight, which renders as the 17th once
  // it is formatted in Hawaiʻi time. Pin date-only values to local noon so the
  // published date reads the same as the one in the structured data.
  const raw = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T12:00:00`) : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
